import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { saveAs } from 'file-saver'

// UI
import { Box, Button, Flex, Heading, HStack, Spacer } from '@chakra-ui/react'

import { NonFormUpload } from '@/components/Elements'

import FullTable from '@/components/Table/FullTable/FullTable'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
//helper
import { createColumnHelper } from '@tanstack/react-table'

import { LoadingSection } from '@/components/Elements'
import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import { ListStatus } from '@/constants/api.constant'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import getGCMaterialTableColumns from '../constants/GCMaterialTableColumns'
import useGCMaterial from '../useGCMaterials'
const GCMaterialListPage = () => {
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:GCMaterial`, {})

  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()

  const {
    useFetchAllGCMaterials,
    useCreateGCMaterial,
    useRemoveGCMaterial,
    useUpdateGCMaterial,
    useUploadGCMaterials,
    useDownloadGCMaterials,
  } = useGCMaterial()

  const { data: GCMaterialsResponse, isLoading, refetch: refetchGCMaterial } = useFetchAllGCMaterials(query)

  const removeGCMaterial = useRemoveGCMaterial()
  const updateGCMaterial = useUpdateGCMaterial()
  const uploadGCMaterials = useUploadGCMaterials()
  const downloadGCMaterials = useDownloadGCMaterials()

  // Handler

  const handleDetail = (row) => {
    const GCMaterialId = row?.original?._id
    navigate(`/admin/GCMaterials/${GCMaterialId}`)
  }

  const handleDeleteGCMaterial = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())

        await updateGCMaterial.mutateAsync({ id, data: { status: ListStatus.INACTIVE } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, removeGCMaterial]
  )

  const handleActivateGCMaterial = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())
        await updateGCMaterial.mutateAsync({ id, data: { status: ListStatus.ACTIVE } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, updateGCMaterial]
  )

  const handleUpload = async (file) => {
    try {
      if (!file) {
        toast.error('No File Uploaded')
        return
      }
      dispatch(showLoadingOverlay())

      const res = await uploadGCMaterials.mutateAsync(file)

      if (res.data) {
        console.log(res.data)
        const skippedCount = res.data.skippedCount || 0
        const totalCount = res.data.totalCount || 0

        toast.success(
          `Successfully uploaded ${totalCount - skippedCount} GCMaterials. Skipped ${skippedCount} invalid entries.`
        )
      }

      console.log(res)
    } catch (err) {
      toast.error('Error uploading file')
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleDownload = useCallback(async () => {
    try {
      dispatch(showLoadingOverlay())
      const response = await downloadGCMaterials.mutateAsync()

      const contentDisposition = response.headers['content-disposition']
      let fileName = 'E-Approval GC Material.csv'

      if (contentDisposition && contentDisposition.includes('filename=')) {
        fileName = contentDisposition.split('filename=')[1].replace(/"/g, '')
      }

      const csvBlob = new Blob([response.data], {
        type: response.headers['content-type'] || 'text/csv;charset=utf-8',
      })
      saveAs(csvBlob, fileName)
    } catch (err) {
      toast.error('Error downloading GC Materials')
      console.log(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }, [dispatch, downloadGCMaterials])

  const defaultColumns = getGCMaterialTableColumns({
    handleActivateGCMaterial,
    handleDeleteGCMaterial,
  })

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <Flex alignItems="center" p={5} borderBottom="1px solid #d8dbe0" gap="3">
        <Heading>GC Material ({GCMaterialsResponse?.data?.count ?? 0})</Heading>

        <Flex direction={'column'} gap={2}>
          <HStack>
            <Button colorScheme="gray" onClick={handleDownload}>
              Download
            </Button>
            <NonFormUpload uploadHandler={handleUpload} />
          </HStack>
        </Flex>

        <Spacer />
        <Link to="/admin/GCMaterials/create">
          <Button colorScheme="blue">Create GC Material</Button>
        </Link>
      </Flex>
      <Box p={5}>
        <FullTable
          data={Array.isArray(GCMaterialsResponse?.data?.results) ? GCMaterialsResponse.data.results : []}
          rowCount={GCMaterialsResponse?.data?.count}
          columnDef={defaultColumns}
          state={storedTableState}
          setState={setStoredTableState}
          handleRowClick={handleDetail}
        />
      </Box>
    </Box>
  )
}

export default GCMaterialListPage
